import * as React from 'react';
import { Checkbox, DefaultPalette, ICommandBarItemProps, Panel } from '@fluentui/react';
import TimeTaxSupportSystemClient from '../../system';
import { DateTime } from 'luxon';
import MessageCourier from '../../lib/MessageCourier';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { defaultCommandBarButtonStyles, titleCommandBarButtonStyles } from '../../styles/button-styles';
import { ReportingFiltersPeriodo } from './ReportingFiltersPeriodo';
import { useTranslation } from 'react-i18next';
import { Button } from 'devextreme-react/button';
import DataGrid, { Scrolling, Column, Summary, SortByGroupSummaryInfo, Export, SearchPanel, TotalItem } from 'devextreme-react/data-grid';
import { User } from '../../system/User';
import { EscalableExporterOptions, exportToExcel, getCorporativoInfo, parseDateTimeForList } from '../../lib/Util';
import Loading from '../Spinners/Loading';
import { IorganizationLevels } from '../../system/AuthenticationSystem';
import { useBoolean } from '@fluentui/react-hooks';
import { ExportingEvent } from 'devextreme/ui/data_grid';
import { IReportActivity } from '../../system/reporting/reporte-actividad';
import { formatPercentage } from '../../lib/formatters';
import InfoHourDecimalTooltip from '../../shared/components/InfoHourDecimalTooltip';

interface IReportingHoursProjects {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (state: boolean) => void;
  user: User;
}

type ColumnVisibilityMap = Record<string, boolean>;

const COLUMN_CONFIG = {
  corporativo: {
    key: 'col_corporativo',
    label: 'Corporativo',
    defaultVisible: true,
  },
  periodo: {
    key: 'periodo',
    label: 'Periodo',
    defaultVisible: true,
  },
  dni: {
    key: 'col_documento_numero',
    label: 'DNI',
    defaultVisible: true,
  },
  col_sap_id: {
    key: 'col_sap_id',
    label: 'SAP ID',
    defaultVisible: true,
  },
  colaborador: {
    key: 'col_nombre_completo',
    label: 'Colaborador',
    defaultVisible: true,
  },
  proyectoCodigo: {
    key: 'pro_codigo',
    label: 'Proyecto o Servicio Codigo',
    defaultVisible: true,
  },
  proyectoNombre: {
    key: 'pro_nombre',
    label: 'Nombre de proyecto o servicio',
    defaultVisible: true,
  },
  pro_agrupador: {
    key: 'pro_agrupador_nombre',
    label: 'Agrupador de liquidación - concepto',
    defaultVisible: true,
  },
  actividadNombre: {
    key: 'act_nombre',
    label: 'Nombre de la actividad',
    defaultVisible: true,
  },
  act_descripcion:{
    key: 'act_descripcion',
    label: 'Descripción de la actividad',
    defaultVisible: true,
  },
  act_id: {
    key: 'act_id',
    label: 'Código de actividad',
    defaultVisible: true,
  },
  actividadInicio: {
    key: 'act_inicio',
    label: 'Fecha y hora de actividad (inicio)',
    defaultVisible: true,
  },
  documentos: {
    key: 'act_adjuntos',
    label: 'Adjuntos a la actividad',
    defaultVisible: true,
  },
  cargo: {
    key: 'col_cargo',
    label: 'Cargo',
    defaultVisible: true,
  },

  email: {
    key: 'col_email',
    label: 'Email',
    defaultVisible: true,
  },

  fechaAlta: {
    key: 'col_fecha_alta',
    label: 'Alta',
    defaultVisible: true,
  },

  fechaBaja: {
    key: 'col_fecha_baja',
    label: 'Baja',
    defaultVisible: true,
  },

  modalidad: {
    key: 'col_baja_motivo',
    label: 'Modalidad',
    defaultVisible: true,
  },
  disfa_empresa: {
    key: 'disfa_empresa',
    label: 'Empresa distribuidas en la actividad',
    defaultVisible: true,
  },
  disfa_porcentaje_decimal: {
    key: 'disfa_porcentaje_decimal',
    label: '% de Distribución',
    defaultVisible: true,
  },
  horas_distribuidas: {
    key: 'horas_distribuidas',
    label: 'Horas ',
    defaultVisible: true,
  },
};

export const ReportingActivities = (props: IReportingHoursProjects) => {
  const { t } = useTranslation();
  const [period, setPeriod] = React.useState<DateTime | undefined>(undefined);
  const [sectorId, setSectorId] = React.useState<string>('');
  const [collaboratorId, setCollaboratorId] = React.useState<string>('');
  const [items, setItems] = React.useState<IReportActivity[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [levelsN, setLevelsN] = React.useState<IorganizationLevels[]>([]);
  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [visibleMap, setVisibleMap] = React.useState<ColumnVisibilityMap>({});
  const [corporativoInfo, setCorporativoInfo] = React.useState<any>(undefined);
  const NRO_ADMIN_TRIBUTARIA_ACC = 20101024645;

  /* ============================
     CARGA NIVELES
  ============================ */
  React.useEffect(() => {
    const loadLevels = async () => {
      setLoading(true);
      const data = await props.system.getOrganizationLevels();
      setLevelsN(data);
      setLoading(false);
    };
    loadLevels();
    const corporativoInfoData = getCorporativoInfo();
    setCorporativoInfo(corporativoInfoData?.empp_nro_admin_tributaria);

  }, []);

  const itemsMenu = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Reporte de actividades'),
        iconProps: {
          iconName: 'ReminderPerson',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
      },
    ],
    [],
  );

  /* ============================
     HELPERS
  ============================ */
  const getVisible = React.useCallback((key: string) => visibleMap[key] ?? false, [visibleMap]);

  const buildInitialVisibility = (levels: IorganizationLevels[]) => {
    const map: ColumnVisibilityMap = {};

    Object.values(COLUMN_CONFIG).forEach((c) => {
      map[c.key] = c.defaultVisible;
    });

    levels.forEach((n) => {
      map[n.nio_nombre_tec] = true;
    });

    setVisibleMap(map);
  };

  const markAll = () => setVisibleMap((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, true])));

  const unmarkAll = () => setVisibleMap((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, false])));

  const onDownload = async (id: string, name: string) => {
    try {
      props.changeBlockUI(true);
      const downloadUrl = await props.system.getDocumentSystem().getDownloadLinkById(id);
      props.courier.messageSuccess(`${t('Se a descargado el archivo')} ${name}`);
      window.open(downloadUrl, '_blank');
    } catch (e) {
      props.courier.messageError(e);
    } finally {
      props.changeBlockUI(false);
    }
  };

  /* ============================
     SEARCH
  ============================ */
  const onClickSearch = async () => {
    props.changeBlockUI(true);
    try {
      if (!period) return;
      setItems([]);

      const data = await props.system.getReportingSystem().getActivityReport(period, sectorId, collaboratorId);

      setItems(data);
      buildInitialVisibility(levelsN);
      if (data.length === 0) {
        props.courier.messageInfo(t('No se encontraron resultados para el periodo seleccionado'));
        return;
      }

      props.courier.messageSuccess(t('Reporte generado con éxito'));
    } catch (e) {
      props.courier.messageError(e);
    } finally {
      props.changeBlockUI(false);
    }
  };
  const onExporting = (e: ExportingEvent<IReportActivity, any>) => {
    let options: EscalableExporterOptions = {
      fileName: `reporte-de-actividades-del-periodo-${period?.toFormat('LL-yyyy')}`,
      sheetName: t('Reporte de actividades'),
      customizeCell: (options) => {
        if (options.gridCell?.rowType === 'group') {
          options.excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '8DB4E2' } };
        }
        if (options.gridCell?.rowType === 'totalFooter' && options.excelCell.value) {
          options.excelCell.font.italic = true;
        }
      },
    };
    exportToExcel(e, options);
  };

  if (loading) {
    return <Loading title="Cargando" description="Por favor espere estamos procesando la información necesaria para completar su solicitud." />;
  }

  console.log("val-corporativoInfo", corporativoInfo);
  console.log("val-NRO_ADMIN_TRIBUTARIA_ACC", NRO_ADMIN_TRIBUTARIA_ACC);

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={""}
      items={itemsMenu}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="highligth"
      titleBody={t("Reporte de actividades")}
    >
      <div className="padding-1 bg-white">
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row">
            <ReportingFiltersPeriodo
              system={props.system}
              courier={props.courier}
              onChangePeriodDate={(date) => setPeriod(date)}
              onChangeSectorId={(id) => setSectorId(id)}
              onChangeCollaboratorId={(id) => setCollaboratorId(id)}
              onClickSearch={onClickSearch}
              user={props.user}
            />
          </div>
          {items.length > 0 && (
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12">
                <Button
                  className="btn-dinamic-table"
                  id="dinamic-btn"
                  icon="inserttable"
                  onClick={openPanel}
                  style={{
                    marginRight: 12,
                  }}
                />
                <DataGrid
                  height={620}
                  width="100%"
                  keyExpr={["act_id", "col_id", "disfa_empid"]}
                  dataSource={items}
                  showBorders={true}
                  wordWrapEnabled={true}
                  onExporting={onExporting}
                >
                  <Export enabled={true} />
                  <Scrolling mode="virtual" />
                  <SearchPanel visible={true} placeholder={t("Buscar...")} />

                  <Column
                    minWidth={160}
                    dataField={COLUMN_CONFIG.corporativo.key}
                    caption={t(COLUMN_CONFIG.corporativo.label)}
                    visible={getVisible(COLUMN_CONFIG.corporativo.key)}
                    groupCellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={80}
                    dataField={COLUMN_CONFIG.periodo.key}
                    caption={t(COLUMN_CONFIG.periodo.label)}
                    visible={getVisible(COLUMN_CONFIG.periodo.key)}
                    groupCellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  {levelsN.map((item) => {
                    return (
                      <Column
                        key={item.nio_id}
                        minWidth={100}
                        cellRender={(d) => {
                          return (
                            <div className="dx-datagrid-text-content dx-datagrid-group-text-content text-overflow">
                              {d?.value}
                            </div>
                          );
                        }}
                        dataField={item.nio_nombre_tec}
                        caption={t(item.nio_nombre)}
                        width={150}
                        visible={getVisible(item.nio_nombre_tec)}
                      />
                    );
                  })}

                  {corporativoInfo &&
                    (corporativoInfo == NRO_ADMIN_TRIBUTARIA_ACC ? (
                      <Column
                        minWidth={100}
                        dataField={COLUMN_CONFIG.col_sap_id.key}
                        caption={t(COLUMN_CONFIG.col_sap_id.label)}
                        visible={getVisible(COLUMN_CONFIG.col_sap_id.key)}
                        groupCellRender={(d) => (
                          <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                            {d.value}
                          </div>
                        )}
                      />
                    ) : (
                      <Column
                        minWidth={100}
                        dataField={COLUMN_CONFIG.dni.key}
                        caption={t(COLUMN_CONFIG.dni.label)}
                        visible={getVisible(COLUMN_CONFIG.dni.key)}
                        groupCellRender={(d) => (
                          <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                            {d.value}
                          </div>
                        )}
                      />
                    ))}
                  <Column
                    minWidth={250}
                    dataField={COLUMN_CONFIG.colaborador.key}
                    caption={t(COLUMN_CONFIG.colaborador.label)}
                    visible={getVisible(COLUMN_CONFIG.colaborador.key)}
                    alignment={"center"}
                    width={200}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={140}
                    dataField={COLUMN_CONFIG.proyectoCodigo.key}
                    caption={t(COLUMN_CONFIG.proyectoCodigo.label)}
                    visible={getVisible(COLUMN_CONFIG.proyectoCodigo.key)}
                    alignment={"center"}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={160}
                    dataField={COLUMN_CONFIG.proyectoNombre.key}
                    caption={t(COLUMN_CONFIG.proyectoNombre.label)}
                    visible={getVisible(COLUMN_CONFIG.proyectoNombre.key)}
                    alignment={"center"}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  {corporativoInfo &&
                    corporativoInfo != NRO_ADMIN_TRIBUTARIA_ACC && (
                      <Column
                        minWidth={120}
                        dataField={COLUMN_CONFIG.pro_agrupador.key}
                        caption={t(COLUMN_CONFIG.pro_agrupador.label)}
                        visible={getVisible(COLUMN_CONFIG.pro_agrupador.key)}
                        alignment="center"
                        cellRender={(d) => (
                          <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                            {d.value}
                          </div>
                        )}
                      />
                    )}

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.act_id.key}
                    caption={t(COLUMN_CONFIG.act_id.label)}
                    visible={getVisible(COLUMN_CONFIG.act_id.key)}
                    width={100}
                    groupCellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={180}
                    dataField={COLUMN_CONFIG.actividadNombre.key}
                    caption={t(COLUMN_CONFIG.actividadNombre.label)}
                    visible={getVisible(COLUMN_CONFIG.actividadNombre.key)}
                    alignment={"center"}
                    width={300}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={140}
                    dataField={COLUMN_CONFIG.act_descripcion.key}
                    caption={t(COLUMN_CONFIG.act_descripcion.label)}
                    visible={getVisible(COLUMN_CONFIG.act_descripcion.key)}
                    alignment={"center"}
                    width={340}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.disfa_empresa.key}
                    caption={t(COLUMN_CONFIG.disfa_empresa.label)}
                    visible={getVisible(COLUMN_CONFIG.disfa_empresa.key)}
                    alignment={"center"}
                    width={300}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.disfa_porcentaje_decimal.key}
                    caption={t(COLUMN_CONFIG.disfa_porcentaje_decimal.label)}
                    visible={getVisible(
                      COLUMN_CONFIG.disfa_porcentaje_decimal.key,
                    )}
                    calculateCellValue={(item: IReportActivity) =>
                      formatPercentage(item.disfa_porcentaje_decimal)
                    }
                    calculateSortValue={(item: IReportActivity) => item.disfa_porcentaje_decimal }
                    alignment={"center"}
                    width={100}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={140}
                    dataField={COLUMN_CONFIG.actividadInicio.key}
                    caption={t(COLUMN_CONFIG.actividadInicio.label)}
                    visible={getVisible(COLUMN_CONFIG.actividadInicio.key)}
                    calculateCellValue={(rowData: IReportActivity) =>
                      parseDateTimeForList(props.system, rowData.act_inicio)
                    }
                    alignment={"center"}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.horas_distribuidas.key}
                    headerCellRender={() => {
                      return (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {t(COLUMN_CONFIG.horas_distribuidas.label)}
                          <InfoHourDecimalTooltip />
                        </div>
                      );
                    }}
                    visible={getVisible(COLUMN_CONFIG.horas_distribuidas.key)}
                    alignment={"center"}
                    width={100}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={200}
                    dataField={COLUMN_CONFIG.documentos.key}
                    caption={t(COLUMN_CONFIG.documentos.label)}
                    visible={getVisible(COLUMN_CONFIG.documentos.key)}
                    dataType="number"
                    alignment={"center"}
                    calculateCellValue={(rowData: IReportActivity) =>
                      rowData.act_adjuntos.map((d) => d.doc_nombre).join(" , ")
                    }
                    width={340}
                    cellRender={(d) => (
                      <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                        {d.data.act_adjuntos?.map((item, index, arr) => (
                          <span key={item.doc_id}>
                            <button
                              className="link-button"
                              onClick={() =>
                                onDownload(item.doc_id, item.doc_nombre)
                              }
                            >
                              {item.doc_nombre}
                            </button>
                            {index < arr.length - 1 && " , "}
                          </span>
                        ))}
                      </div>
                    )}
                  />

                  <Column
                    minWidth={200}
                    dataField={COLUMN_CONFIG.cargo.key}
                    caption={t(COLUMN_CONFIG.cargo.label)}
                    visible={getVisible(COLUMN_CONFIG.cargo.key)}
                    alignment={"center"}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={220}
                    dataField={COLUMN_CONFIG.email.key}
                    caption={t(COLUMN_CONFIG.email.label)}
                    visible={getVisible(COLUMN_CONFIG.email.key)}
                    alignment={"center"}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.fechaAlta.key}
                    caption={t(COLUMN_CONFIG.fechaAlta.label)}
                    visible={getVisible(COLUMN_CONFIG.fechaAlta.key)}
                    alignment={"center"}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.fechaBaja.key}
                    caption={t(COLUMN_CONFIG.fechaBaja.label)}
                    visible={getVisible(COLUMN_CONFIG.fechaBaja.key)}
                    alignment={"center"}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.modalidad.key}
                    caption={t(COLUMN_CONFIG.modalidad.label)}
                    visible={getVisible(COLUMN_CONFIG.modalidad.key)}
                    alignment={"center"}
                    cellRender={(d) => {
                      return (
                        <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">
                          {d.value}
                        </div>
                      );
                    }}
                  />

                  <Summary
                    calculateCustomSummary={(options) => {
                      if (options.name === "activitiesByCollaborator") {
                        if (options.summaryProcess === "start") {
                          options.totalValue = new Set<string>();
                        }

                        if (options.summaryProcess === "calculate") {
                          const row = options.value as IReportActivity;
                          if (row.act_id && row.col_id) {
                            const key = `${row.act_id}_${row.col_id}`;
                            options.totalValue.add(key);
                          }
                        }

                        if (options.summaryProcess === "finalize") {
                          options.totalValue = options.totalValue.size;
                        }
                      }
                    }}
                  >
                    <TotalItem
                      name="activitiesByCollaborator"
                      showInColumn={COLUMN_CONFIG.act_id.key}
                      summaryType="custom"
                      displayFormat={`${t("Total de actividades")}: {0}`}
                    />
                    <TotalItem
                      column={COLUMN_CONFIG.horas_distribuidas.key}
                      summaryType="sum"
                      valueFormat={{
                        type: "fixedPoint",
                        precision: 2,
                      }}
                      displayFormat={`${t("Total de horas distribuidas")}: {0}`}
                    />
                  </Summary>
                  <SortByGroupSummaryInfo
                    sortOrder={"desc"}
                    summaryItem="sum"
                  />
                </DataGrid>
              </div>
            </div>
          )}
        </div>

        <Panel
          headerText={t("Tabla dinamica")}
          isOpen={isOpen}
          onDismiss={dismissPanel}
          closeButtonAriaLabel={t("Close")}
        >
          <div>
            <p>{t("Selecciona las columnas que quieres ver")}</p>
            <span className="d-block link-styles" onClick={markAll}>
              {t("Marcar todas")}
            </span>
            <span className="d-block link-styles mb-2" onClick={unmarkAll}>
              {t("Desmarcar todas")}
            </span>
            {Object.values(COLUMN_CONFIG)
              .filter((c) =>
                corporativoInfo == NRO_ADMIN_TRIBUTARIA_ACC
                  ? c.key !== COLUMN_CONFIG.dni.key &&
                    c.key !== COLUMN_CONFIG.pro_agrupador.key
                  : c.key !== COLUMN_CONFIG.col_sap_id.key,
              )
              .map((c) => (
                <Checkbox
                  className="d-block mt-1"
                  key={c.key}
                  label={t(c.label)}
                  checked={visibleMap[c.key]}
                  onChange={() =>
                    setVisibleMap((prev) => ({
                      ...prev,
                      [c.key]: !prev[c.key],
                    }))
                  }
                />
              ))}

            {levelsN.map((n) => (
              <Checkbox
                className="d-block mt-1"
                key={n.nio_nombre_tec}
                label={t(n.nio_nombre)}
                checked={visibleMap[n.nio_nombre_tec]}
                onChange={() =>
                  setVisibleMap((prev) => ({
                    ...prev,
                    [n.nio_nombre_tec]: !prev[n.nio_nombre_tec],
                  }))
                }
              />
            ))}
          </div>
        </Panel>
      </div>
    </ViewWrapperWithCommandBar>
  );
};
